<template>
  <div>
    <template v-for="hotspot in hotspots">
      <transition :key="hotspot.key" :name="working ? 'fade' : 'fast'">
        <div
          class="hotspot"
          :class="{ big: hotspot.size !== 1 }"
          v-if="hotspot.show"
          :style="{
            margin: hotspot.margin,
            left: hotspot.left,
            right: hotspot.right,
            bottom: `calc(${hotspot.bottom} - ${
              hotspot.size !== 1 ? 22.5 : 17
            }px)`,
          }"
        >
          {{ $t(`CTA${hotspot.name}Title_${product.model}`) }}
        </div>
      </transition>
    </template>

    <div class="toolbar form-switch">
      <span class="toolbar__text">
        {{ $t("firstPerson") }}
      </span>
      <input
        class="form-check-input toolbar__switch"
        type="checkbox"
        role="switch"
        v-model="thirdPerson"
        @change="changePerspectiveCk"
      />
      <span class="toolbar__text">
        {{ $t("thirdPerson") }}
      </span>
    </div>

    <div class="skipButton" @click="setSkipLoad(true)" v-if="showSkip">
      {{ $t("skip") }}
    </div>

    <div class="menuButton" @click="openWelcome()" v-if="showMenu">
      {{ $t("menu") }}
    </div>

    <canvas ref="canvas" id="canvas" />
  </div>
</template>

<script>
import {
  createScene,
  runScene,
  stopScene,
  createDot,
  cameraHelper,
  startTest,
  loadProduct,
  hideProduct,
  powerOffProduct,
  powerOnProduct,
  aerialView,
  createHotspot,
  showAnimation,
  loadControlRoom,
  setCamera,
  changePerspective,
  calculatePixel,
  actionCTA,
  setSkipLoad,
} from "@/scenes/MyFirstScene";

export default {
  name: "BabylonComponent",
  data() {
    return {
      showSkip: false,
      showMenu: false,
      skipLoad: false,
      thirdPerson: true,
      product: null,
      productShowing: false,
      working: false,
      hotspots: [
        {
          key: "power",
          name: "power",
          position: {
            Chill_Lab: [1.2, 2.2, 4.8],
            Indoor_Lab: [4.08, 2.2, 8.3],
          },
          size: 1,
          show: false,
          left: null,
          right: null,
          bottom: 0,
        },
        {
          key: "hydraulic",
          name: "hydraulic",
          position: {
            Chill_Lab: [-4.5, 2.2, -4.8],
            Indoor_Lab: [6.01, 2.2, -4.6],
          },
          size: 1,
          show: false,
          left: null,
          right: null,
          bottom: 0,
        },
        {
          key: "thermoregulation",
          name: "thermoregulation",
          position: {
            Chill_Lab: [-3.5, 2.5, -3.5],
            Indoor_Lab: [8.6, 4.4, 0.25],
          },
          size: {
            Chill_Lab: 1,
            Indoor_Lab: 1.5,
          },
          show: false,
          left: null,
          right: null,
          bottom: 0,
        },
        {
          key: "airDuct",
          name: "airDuct",
          position: {
            Indoor_Lab: [-7.7, 3.5, 0.25],
          },
          size: 1.5,
          show: false,
          left: null,
          right: null,
          bottom: 0,
        },
        {
          key: "control",
          name: "control",
          position: {
            Chill_Lab: [1.1, 2.2, -4.8],
            Indoor_Lab: [-3.86, 2.2, -4.6],
          },
          size: 1,
          show: false,
          left: null,
          right: null,
          bottom: 0,
        },
        {
          key: "test",
          name: "test",
          position: {
            Chill_Lab: [4.125, 1.67, -0.5],
            Indoor_Lab: [1.25, 1.75, -1],
          },
          size: 1.5,
          show: false,
          left: null,
          right: null,
          bottom: 0,
        },
        {
          key: "host",
          name: "host",
          position: {
            Chill_Lab: [2, 2, -4.274],
            Indoor_Lab: [-0.9, 2, -4.4],
          },
          size: 1,
          show: false,
          left: null,
          right: null,
          bottom: 0,
        },
      ],
    };
  },
  computed: {
    lab() {
      return this.$store.getters.getCurrentLab;
    },
  },
  async mounted() {
    this.$root.$on("dispatchAction", this.dispatchAction);
    window.dispatchAction = this.dispatchAction;

    window.createDot = createDot;
    window.cameraHelper = cameraHelper;

    const canvas = this.$refs.canvas;

    if (canvas) {
      await createScene({
        displayHotspot: this.displayHotspot,
        changePerspectiveCk: this.changePerspectiveCk,
        canvas,
        model: this.lab,
      });

      await runScene();

      this.createHotspots();

      this.$store.dispatch("setSpinner", {
        loading: false,
        spinner: true,
      });
    }
  },
  beforeDestroy() {
    this.$root.$off("dispatchAction", this.dispatchAction);
    stopScene();
  },
  methods: {
    setSkipLoad(value) {
      this.skipLoad = value;
      this.showSkip = false;
      setSkipLoad(value);
    },
    changePerspectiveCk(thirdPerson) {
      if (thirdPerson === true) {
        this.thirdPerson = true;
      } else if (thirdPerson === false) {
        this.thirdPerson = false;
      } else {
        document.getElementById("canvas").focus();
      }

      changePerspective(this.thirdPerson);
    },
    displayHotspot(key, display) {
      const hotspot = this.hotspots.find(
        (hotspot) => hotspot.key === key && hotspot.position[this.lab]
      );

      if (hotspot) {
        if (display) {
          const { right, bottom, width } = calculatePixel(
            `hotspot_${hotspot.key}`
          );

          hotspot.bottom = bottom;

          const translate = parseInt(right) > window.innerWidth / 2;
          hotspot.right = translate ? null : right;
          hotspot.left = translate
            ? window.innerWidth - parseInt(right) + "px"
            : null;
          hotspot.margin = `0 ${width / 2 + 10}px`;
          hotspot.show = true;
        } else {
          setTimeout(() => {
            hotspot.show = false;
          }, 100);
        }
      }
    },
    createHotspots() {
      this.hotspots.forEach((hotspot) => {
        const position = hotspot.position[this.lab];
        const size = hotspot.size[this.lab] || hotspot.size;

        if (position) {
          createHotspot(
            hotspot.key,
            size,
            position,
            this.showHotspot,
            this.hideHotspot,
            this.clickHotspot
          );

          if (["thermoregulation", "airDuct"].includes(hotspot.key)) {
            hotspot.size = 1;
          }

          setTimeout(() => {
            hotspot.show = false;
          }, 100);
        }
      });
    },
    displayHotspots(display) {
      this.hotspots.forEach((hotspot) => {
        if (hotspot.position[this.lab]) {
          showAnimation(`hotspot_${hotspot.key}`, display, 0.5);
        }
      });
      this.showMenu = display;
    },
    showHotspot(key, right, bottom, width) {
      if (this.working) {
        return;
      }
      const hotspot = this.hotspots.find((el) => el.key === key);
      hotspot.bottom = bottom;

      const translate = parseInt(right) > window.innerWidth / 2;
      hotspot.right = translate ? null : right;
      hotspot.left = translate
        ? window.innerWidth - parseInt(right) + "px"
        : null;
      hotspot.margin = `0 ${width / 2 + 10}px`;
      hotspot.show = true;
    },
    hideHotspot(key) {
      if (this.working) {
        return;
      }
      const hotspot = this.hotspots.find((el) => el.key === key);
      setTimeout(() => {
        hotspot.show = false;
      }, 100);
    },
    clickHotspot(cta) {
      if (this.working) {
        return;
      }

      const hotspot = this.hotspots.find((el) => el.key === cta);
      setTimeout(() => {
        hotspot.show = false;
      }, 100);

      this.dispatchAction({ cta: cta });
    },
    async dispatchAction({ cta, value, resolve }) {
      if (cta === "host") {
        this.openWelcome();
      } else if (cta === "aerialView") {
        this.aerialView(resolve);
      } else if (cta === "product") {
        resolve(await this.selectProduct(false, false, "product"));
      } else if (cta === "test") {
        await this.selectTest(true);
      } else if (cta === "displayHotspots") {
        setCamera("Main");
        this.displayHotspots(value);
      } else if (cta === "witnessProductTests") {
        setCamera("Main");
        await this.selectTest(false);
        resolve();
      } else if (
        ["power", "hydraulic", "thermoregulation", "airDuct"].includes(cta)
      ) {
        await this.actionCTA(cta);
      } else if (cta === "control") {
        this.working = true;

        this.displayHotspots(false);
        const title = `CTA${cta}Title_${this.product.model}`;
        const body = `CTA${cta}Body_${this.product.model}`;

        await Promise.all([
          new Promise((resolve) => {
            this.$root.$emit("dialogCTA", { resolve, title, body });
          }),
          loadControlRoom(true),
        ]);

        await loadControlRoom(false);
        this.displayHotspots(true);

        document.getElementById("canvas").focus();
        this.working = false;
      }
    },
    async actionCTA(cta) {
      this.working = true;

      const title = `CTA${cta}Title_${this.product.model}`;
      const body = `CTA${cta}Body_${this.product.model}`;

      const productShowing = this.productShowing;

      if (!productShowing) {
        await this.selectProduct(true, true, cta);
        await Promise.all([
          new Promise((resolve) => {
            this.$root.$emit("dialogCTA", { resolve, title, body });
          }),
        ]);
      } else {
        this.displayHotspots(false);

        await Promise.all([
          actionCTA(true, this.product, cta),
          new Promise((resolve) => {
            this.$root.$emit("dialogCTA", { resolve, title, body });
          }),
        ]);
        await actionCTA(false, this.product, cta);
      }

      // Check if we need to hide or not the product
      if (!productShowing) {
        this.$store.dispatch("setSpinner", {
          loading: true,
        });
        await powerOffProduct(this.product, true);
        await this.hideProduct(true);
        this.$store.dispatch("setSpinner", {
          loading: false,
        });
      }

      this.displayHotspots(true);

      document.getElementById("canvas").focus();
      this.working = false;
    },
    async openWelcome() {
      this.$store.dispatch("setSpinner", {
        loading: true,
      });

      this.displayHotspots(false);

      this.$root.$emit("openAfterWelcome");

      this.$store.dispatch("setSpinner", {
        loading: false,
      });
    },
    async aerialView(resolve) {
      this.working = true;
      await aerialView();
      this.displayHotspots(false);
      this.working = false;
      resolve();
    },
    async selectProduct(force = false, onlyShow = false, mode = "product") {
      if (!force && this.working) return;

      this.working = true;

      this.displayHotspots(false);

      let newProduct;

      if (!onlyShow) {
        // Abrimos el selector de productos

        newProduct = await new Promise((resolve) => {
          this.$root.$emit("openProductSelection", { resolve });
        });
      } else {
        if (this.lab === "Chill_Lab") {
          newProduct = {
            title: "productSelectionTitle_AFC_NA",
            body: "productSelectionBody_AFC_NA",
            confirm: "productSelectionConfirm_AFC_NA",
            model: "AFC_NA",
            image: require("../assets/images/products/AFC_NA.jpg"),
          };
        } else if (this.lab === "Indoor_Lab") {
          newProduct = {
            title: "productSelectionTitle_CWA",
            body: "productSelectionBody_CWA",
            confirm: "productSelectionConfirm_CWA",
            model: "CWA",
            image: require("../assets/images/products/CWA.jpg"),
          };
        }
      }

      if (newProduct) {
        // Ha seleccionado un producto

        let replace = false;

        this.showSkip = true;

        if (this.product) {
          if (this.product.model === newProduct.model) {
            // Product already loaded
            this.working = false;
            this.showSkip = false;
            return true;
          } else {
            replace = true;
            // await powerOffProduct(this.product, force);
            await this.hideProduct(force, replace);
          }
        }

        if (force) {
          this.$store.dispatch("setSpinner", {
            loading: true,
          });
        }

        this.product = newProduct;

        await loadProduct(this.product, force, replace);
        this.productShowing = true;

        if (force) {
          this.$store.dispatch("setSpinner", {
            loading: false,
          });
        }

        this.showSkip = false;
        await powerOnProduct(this.product, force || this.skipLoad, mode);

        this.working = false;
        this.setSkipLoad(false);

        return true;
      } else {
        this.working = false;
        this.setSkipLoad(false);
        return false;
      }
    },
    async hideProduct(force = false, replace = false) {
      this.working = true;

      await hideProduct(this.product, force, replace);

      this.productShowing = false;
      this.working = force;
    },
    async selectTest(fromCTA = false) {
      this.working = true;

      if (fromCTA) {
        this.displayHotspots(false);
      }

      const test = await new Promise((resolve) => {
        this.$root.$emit("openTestSelection", {
          resolve,
          productName: this.product.model,
        });
      });

      if (test) {
        console.log("Test selected");

        let title = `${test.key}PreTestTitle_${this.product.model}`;
        let body = `${test.key}PreTestBody_${this.product.model}`;
        const buttonR = `${test.key}PreTestButton`;

        const continueTest = await new Promise((resolve) => {
          this.$root.$emit("startPreTest", { resolve, title, body, buttonR });
        });

        document.getElementById("canvas").focus();

        if (continueTest && !test.noTest) {
          title = `${test.key}TestTitle_${this.product.model}`;
          body = `${test.key}TestBody_${this.product.model}`;
          let video = test.key;

          if (test.key === "noise") {
            video = `${this.product.model}_${test.key}`;
          }

          if (this.lab === "Indoor_Lab") {
            video = `${this.product.model}_${test.key}`;
          }

          console.log("Test start");
          await Promise.all([
            new Promise((resolve) => {
              this.$root.$emit("startTest", { resolve, title, body, video });
            }),
            startTest(test.key, this.product, this.eventScene),
          ]);
        }

        return this.selectTest(fromCTA);
      }

      if (fromCTA) {
        this.displayHotspots(true);
      }

      this.working = false;
      return false;
    },
    eventScene(event) {
      this.$root.$emit("eventScene", event);
    },
  },
};
</script>
<style lang="scss" scoped>
#canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 4;
  outline: none;
  border: none;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;
}

.skipButton {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 5;
  display: flex;
  flex-direction: row;
  background-color: #fe5b1b;
  border: 1px solid #ffffff;
  border-radius: 5px;
  color: white;
  padding: 0px 10px;
  cursor: pointer;
  font-size: 18px;
}

.menuButton {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 5;
  display: flex;
  flex-direction: row;
  background-color: #fe5b1b;
  border: 1px solid #ffffff;
  border-radius: 5px;
  color: white;
  padding: 0px 10px;
  cursor: pointer;
}

.toolbar {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 5;
  display: flex;
  flex-direction: row;
  font-size: 18px;

  .toolbar__text {
    color: white;
  }

  .toolbar__switch {
    margin: 4px 10px 0 10px;

    &:checked {
      border-color: #fe5b1b;
      background-color: #fe5b1b;
    }
  }
}

.hotspot {
  position: absolute;
  z-index: 5;
  font-family: "Unna";
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 23px;
  display: flex;
  align-items: center;
  color: #fffdfd;
  background: #fe5b1b;
  border: 1px solid #ffffff;
  border-radius: 6px;
  padding: 5px 15px;

  &.big {
    font-size: 25px;
    line-height: 25px;
    border: 2px solid #ffffff;
  }
}

.fade-enter-active {
  transition: opacity 2.5s;
}

.fade-leave-active {
  transition: opacity 0s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.fast-enter-active {
  transition: opacity 0.5s;
}

.fast-leave-active {
  transition: opacity 0.2s;
}

.fast-enter, .fast-leave-to /* .fast-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
